interface SearchNode {
  slug: string;
  title: string;
  aliases?: string[];
}

interface SearchRenderer {
  highlightNodes: (slugs: string[] | null) => void;
}

interface GraphSearchOptions {
  onMatch?: (slugs: string[] | null) => void;
}

export function createGraphSearch(container: HTMLElement, nodes: SearchNode[], renderer: SearchRenderer, options: GraphSearchOptions = {}): { destroy: () => void } {
  const { onMatch } = options;

  const wrapper = document.createElement('div');
  wrapper.className = 'graph-search';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'graph-search__input';
  input.placeholder = 'Search nodes\u2026';
  input.setAttribute('aria-label', 'Search nodes');
  wrapper.appendChild(input);

  const count = document.createElement('span');
  count.className = 'graph-search__count';
  count.style.display = 'none';
  wrapper.appendChild(count);

  container.appendChild(wrapper);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  function doSearch() {
    const query = input.value.trim().toLowerCase();

    if (!query) {
      count.style.display = 'none';
      renderer.highlightNodes(null);
      if (onMatch) onMatch(null);
      return;
    }

    const matches: string[] = [];
    for (const node of nodes) {
      const title = (node.title || '').toLowerCase();
      if (title.includes(query)) {
        matches.push(node.slug);
        continue;
      }
      if (node.aliases) {
        const aliasMatch = node.aliases.some(a => a.toLowerCase().includes(query));
        if (aliasMatch) {
          matches.push(node.slug);
        }
      }
    }

    count.textContent = `${matches.length} / ${nodes.length}`;
    count.style.display = 'inline';

    renderer.highlightNodes(matches.length > 0 ? matches : []);
    if (onMatch) onMatch(matches);
  }

  input.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSearch, 150);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      input.value = '';
      doSearch();
      input.blur();
    }
  });

  return {
    destroy() {
      if (debounceTimer) clearTimeout(debounceTimer);
      wrapper.remove();
    },
  };
}
